import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Dialog } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import GroupBasketItemImage from "./GroupBasketItemImage";
import CloseIcon from "@mui/icons-material/Close";
import CatalogueSearch from "../shop/CatalogueSearch";
import PriceCut from "../PriceCut";
import useMediaQuery from "../../hooks/useMediaQuery";
import { filterSectionsByQuery, normaliseQuery } from "../../utils/menuSearch";
import { blockedHere } from "../../utils/branchAvailability";
import { formatBasketMoney, productQuantitiesOf } from "../../utils/groupBasketView";
import {
  GREEN,
  IconCircle,
  LINE,
  MUTED,
  QuietButton,
  SheetBody,
  SheetHead,
  TEXT,
  sheetPaperProps,
} from "./groupBasketStyles";

/* A dish the branch cannot sell now: shown, but not chosen. */
export const dishUnavailable = (product) => product?.soldOutToday === true || blockedHere(product);

/*
 * The basket restaurant's menu, to choose the caller's own items. The same
 * menu read and search as the storefront (utils/menuSearch); a tap opens the
 * dish to choose its options.
 */
const GroupMenuSheet = ({ open, menu, restaurantName, currency, myLines = [], onPickDish, onClose }) => {
  const fullScreen = useMediaQuery("(max-width: 600px)");
  const [query, setQuery] = useState("");
  const sections = useMemo(() => filterSectionsByQuery(menu.sections, query), [menu.sections, query]);
  const money = (amount) => formatBasketMoney(amount, currency);
  const quantities = useMemo(() => productQuantitiesOf(myLines), [myLines]);
  const searching = normaliseQuery(query).length > 0;

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={fullScreen ? undefined : sheetPaperProps}
    >
      <SheetHead>
        <div>
          <h2>Add your items</h2>
          <p>{restaurantName}</p>
        </div>
        <IconCircle type="button" onClick={onClose} aria-label="Close menu">
          <CloseIcon />
        </IconCircle>
      </SheetHead>
      <SheetBody>
        <CatalogueSearch
          value={query}
          onChange={setQuery}
          placeholder="Search the menu"
          ariaLabel="Search the menu"
          accent={GREEN}
          maxWidth="100%"
          marginBottom="0"
        />
        {menu.status === "loading" && <State>Loading the menu</State>}
        {menu.status === "failed" && (
          <State>
            The menu could not be loaded.
            <QuietButton type="button" onClick={menu.retry} style={{ marginTop: 10 }}>
              Try again
            </QuietButton>
          </State>
        )}
        {menu.status === "ready" && sections.length === 0 && (
          <State>{searching ? "No dish matches that search." : "This menu has no dishes right now."}</State>
        )}
        {sections.map((section) => (
          <Section key={section._id}>
            <h3>{section.title}</h3>
            <Dishes>
              {section.products.map((product) => {
                const unavailable = dishUnavailable(product);
                const quantity = quantities.get(String(product._id)) || 0;
                return (
                  <li key={product._id}>
                    <Dish
                      type="button"
                      disabled={unavailable}
                      onClick={() => onPickDish(product)}
                      aria-label={`${unavailable ? `${product.name}, not available now` : `${quantity ? "Add more" : "Add"} ${product.name}`}${quantity ? `, ${quantity} in your basket` : ""}`}
                    >
                      <GroupBasketItemImage imageUrl={product.image} />
                      <span className="text">
                        <strong>{product.name}</strong>
                        <PriceCut
                          price={product.price}
                          listPrice={product.listPrice}
                          salePrice={product.salePrice}
                          format={money}
                          className="price"
                        />
                        {quantity > 0 && (
                          <span className="in-basket">
                            <CheckCircleOutlineIcon aria-hidden="true" />
                            {quantity} in your basket
                          </span>
                        )}
                        {unavailable && <span className="off">Not available now</span>}
                      </span>
                      {!unavailable && <span className="add" aria-hidden="true">{quantity ? "Add more" : "Add"}</span>}
                    </Dish>
                  </li>
                );
              })}
            </Dishes>
          </Section>
        ))}
      </SheetBody>
    </Dialog>
  );
};

const State = styled.div`
  padding: 24px 0;
  text-align: center;
  font-size: 15px;
  color: ${MUTED};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Section = styled.section`
  h3 {
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${MUTED};
  }
`;

const Dishes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li + li {
    border-top: 1px solid ${LINE};
  }
`;

const Dish = styled.button`
  width: 100%;
  min-height: 72px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border: none;
  background: none;
  text-align: left;
  font-family: inherit;
  color: ${TEXT};
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  strong {
    font-size: 15px;
    font-weight: 800;
    overflow-wrap: anywhere;
  }

  .price {
    font-size: 14px;
    font-weight: 700;
    color: ${MUTED};
  }

  .in-basket {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 7px;
    border-radius: 8px;
    background: #eef5e4;
    color: ${GREEN};
    font-size: 12px;
    font-weight: 800;

    svg { font-size: 16px; }
  }

  .off {
    font-size: 13px;
    font-weight: 700;
    color: #8a5a00;
  }

  .add {
    flex-shrink: 0;
    min-width: 64px;
    padding: 0 10px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: ${GREEN};
    color: #ffffff;
    font-size: 14px;
    font-weight: 800;
  }

  &:hover:not(:disabled) .add {
    background: #335a09;
    color: #ffffff;
  }
`;

export default GroupMenuSheet;
